import { useCallback, useMemo, useRef, useState } from "react";

import { emptyManualEntryState, emptyManualProjectRow, emptyUploadEntryState } from "../../../../../constants";
import {
  LandholdingDetails,
  NewProjectJourneyStep,
  ParsedRowError,
  ProjectDetailsStepState,
  ProjectEntryMethod,
  ProjectRow,
  SelectData,
} from "../../../../../models";
import { ServiceError } from "../../../../../service/Shared";
import { useBulkUploadModal, useEntryMethodSwitch } from "../hooks";
import {
  NewProjectJourneyCorners,
  NewProjectJourneyVariant,
  useNewProjectJourneyContext,
} from "../NewProjectJourneyContext";
import {
  callProjectDetails,
  getEnteredRowCount,
  getLandholdingErrors,
  groupBulkErrorsByRowKey,
  hasCreatedAnything,
  hasEnteredData,
  isPartiallyCreated as getIsPartiallyCreated,
  labelBulkErrorsByRow,
  mapBulkErrorToParsedRowError,
  mergeReplacedUploadRows,
  reconcileRowSelections,
  SubmitProjectDetailsResult,
} from "../utils";

interface UseProjectDetailsStepReturnData {
  variant: NewProjectJourneyVariant;
  corners: NewProjectJourneyCorners;
  entryMethodResetKey: number;
  entryMethod: ProjectEntryMethod;
  isLandholding: boolean;
  rows: ProjectRow[];
  uploadRowErrors: ParsedRowError[];
  landholding: LandholdingDetails;
  formErrors: ServiceError[];
  rowErrors: Record<string, ServiceError[]>;
  landholdingErrors: ServiceError[];
  showSwitchModal: boolean;
  showUploadModal: boolean;
  isParsing: boolean;
  isSubmitting: boolean;
  /** Every row has been persisted by a save, so they can be reviewed here but no longer edited. */
  isLocked: boolean;
  /**
   * A save wrote some of an upload's requests and was refused on the rest. The file can still be
   * replaced to correct them, but not the entry method switched - that would discard what exists.
   */
  isPartiallyCreated: boolean;
  /**
   * The inputs must not take an edit: locked, or a create in flight. Validation can be abandoned by
   * an edit at no cost, but a create cannot - the api would still write the rows while this step
   * threw the answer away, and the next Save would collide on their uuids (BR-02888).
   */
  isReadOnly: boolean;
  /** "Save" when this step is the whole journey, "Next" when the access step follows. */
  nextLabel: string;
  /** What the Next/Save request is doing, for the parsed table to say while it runs. */
  submittingMessage: string;
  parseError: string | undefined;
  showOutOfDateTemplateHint: boolean;
  switchProjectCount: number;
  optionsLoadFailed: boolean;
  verifierOptions: SelectData;
  countryOptions: SelectData;
  getStandardOptions: (projectTypeUuid: string | null) => SelectData;
  getProjectTypeOptions: (standardUuid: string | null) => SelectData;
  handleEntryMethodChange: (method: ProjectEntryMethod) => void;
  handleConfirmDiscard: () => void;
  handleKeepEntries: () => void;
  handleLandholdingChoiceChange: (isLandholding: boolean) => void;
  handleLandholdingChange: (details: LandholdingDetails) => void;
  handleRowChange: (row: ProjectRow) => void;
  handleAddProject: () => void;
  handleRemoveProject: (key: string) => void;
  handleDownloadTemplate: () => void;
  handleFileSelected: (file: File | null) => void;
  handleUpload: () => void;
  handleOpenUploadModal: () => void;
  handleCancelUpload: () => void;
  handleRetryOptions: () => void;
  handleNext: () => void;
}

const UPLOAD_REQUIRED_ERROR: ServiceError = {
  code: "VALIDATION_ATTRIBUTE_REQUIRED",
  message: "Upload a file before continuing.",
};

export const useProjectDetailsStep = (): UseProjectDetailsStepReturnData => {
  const {
    state,
    steps,
    updateDetails,
    setCreatedProjects,
    completeStep,
    clearJourney,
    onComplete,
    options,
    variant,
    corners,
  } = useNewProjectJourneyContext();
  const [isSubmitting, setIsSubmitting] = useState(false);
  // Replacing the file or pressing Next again must abandon a submit already in flight, so a slow
  // first answer cannot arrive after a newer one and step the journey forward on stale rows.
  const submitRequestIdRef = useRef(0);

  const { details } = state;
  const { rows, landholding } = details.data;
  const { validationErrors } = details;
  const isManual = details.entryMethod === "manual";
  const isLandholding = isManual && details.isLandholding;

  // A save created every one of these rows and then failed, on the invitations. The create cannot be
  // undone from here, and re-posting the same row uuids would collide on it (BR-02888), so the rows
  // are shown read-only, and Next either returns to the access step to try again or, when this step
  // is the whole journey, falls through to a save with nothing left to send.
  //
  // An upload of several landholdings is several creates, so a save can write some and be refused
  // on the rest. That is only partly locked: the refused rows still need correcting, so the file
  // can be replaced, and the replacement keeps what was written (mergeReplacedUploadRows).
  const isPartiallyCreated = useMemo(
    () => getIsPartiallyCreated(details, state.createdProjects),
    [details, state.createdProjects]
  );
  const isLocked = state.createdProjects !== null && !isPartiallyCreated;

  // When this is the last step there is no access step to hand over to, so Next writes the
  // projects itself (validateOnly false) and completes the journey. A host embedding just this
  // step - the deal journey's Add project panel - gets its projects created without a second step.
  const isLastStep = steps[steps.length - 1].key === NewProjectJourneyStep.DETAILS;

  // Only a final-step submit writes; a validate-only one is safe to abandon, so those inputs stay live.
  const isReadOnly = isLocked || (isLastStep && isSubmitting);

  /*
    Every error view is derived from the one raw list on the journey state: the summary names the
    project each failure came from, because on its own "Standard is a required field" does not say
    which of five projects is missing one; a typed row shows its own against its fields; an uploaded
    row shows its own in the parsed table - the only way to find the bad one in a file of many; and
    a landholding create validates the landholding alongside its rows, so those are pulled out and
    shown against the landholding's own fields.
  */
  const formErrors = useMemo(() => {
    const labelled = labelBulkErrorsByRow(validationErrors, rows);
    if (isManual || rows.length === 0) return labelled;

    // Uploaded rows carry their own failures in the parsed table, so the box would only repeat
    // them. It keeps what no row can carry: a payload-level refusal, or a request that never went
    // through - otherwise Next would fail with nothing on screen to say why.
    return labelled.filter((_, index) => mapBulkErrorToParsedRowError(validationErrors[index], rows) === null);
  }, [isManual, validationErrors, rows]);
  const rowErrors = useMemo(
    () => (isManual ? groupBulkErrorsByRowKey(validationErrors, rows) : {}),
    [isManual, validationErrors, rows]
  );
  const landholdingErrors = useMemo(
    () => (isLandholding ? getLandholdingErrors(validationErrors) : []),
    [isLandholding, validationErrors]
  );
  const uploadRowErrors = useMemo(
    () =>
      isManual
        ? []
        : validationErrors
            .map((error) => mapBulkErrorToParsedRowError(error, rows))
            .filter((rowError): rowError is ParsedRowError => rowError !== null),
    [isManual, validationErrors, rows]
  );

  const setValidationErrors = useCallback(
    (errors: ServiceError[]) => {
      updateDetails((prev) =>
        prev.validationErrors.length === 0 && errors.length === 0 ? prev : { ...prev, validationErrors: errors }
      );
    },
    [updateDetails]
  );

  const clearErrors = useCallback(() => setValidationErrors([]), [setValidationErrors]);

  // The inputs stay editable while a validate-only submit is in flight (isReadOnly), so its answer
  // can otherwise land after an edit it never saw - passing stale rows or landholding details, or
  // overwriting errors that no longer match what is on screen. Nothing else re-triggers it, so an
  // edit has to abandon it itself.
  const abandonPendingSubmit = useCallback(() => {
    submitRequestIdRef.current += 1;
    setIsSubmitting(false);
  }, []);

  // Every edit to what will be sent goes through here. It is stale for any submit in flight, and
  // for any create the access step's save already made - otherwise that save would reuse a uuid
  // map minted before the edit, silently skipping both the edit and any row added since.
  const editDetails = useCallback(
    (updater: (prev: ProjectDetailsStepState) => ProjectDetailsStepState) => {
      abandonPendingSubmit();
      setCreatedProjects(null);
      updateDetails(updater);
    },
    [abandonPendingSubmit, setCreatedProjects, updateDetails]
  );

  const switchEntryMethod = useCallback(
    (method: ProjectEntryMethod) => {
      // Manual and upload rows share one bucket, so switching starts it fresh for the method being
      // switched to - whatever it held was either empty or has just been confirmed as discarded.
      editDetails((prev) => ({
        ...prev,
        entryMethod: method,
        data: method === "manual" ? emptyManualEntryState() : emptyUploadEntryState(),
        validationErrors: [],
      }));
    },
    [editDetails]
  );

  const entryMethodSwitch = useEntryMethodSwitch({
    currentMethod: details.entryMethod,
    hasEnteredData: () => hasEnteredData(details, details.entryMethod),
    onSwitch: switchEntryMethod,
  });

  // Parsing reports nothing about the rows themselves, so a fresh file starts with no errors - the
  // api fills them in on Next. After a partial save the replacement only corrects what was refused:
  // what was written is kept, with the record of it, so a retry does not post it again (BR-02888).
  const handleParsedRows = useCallback(
    (parsedRows: ProjectRow[]) => {
      if (!isPartiallyCreated) {
        editDetails((prev) => ({ ...prev, data: { ...prev.data, rows: parsedRows }, validationErrors: [] }));
        return;
      }

      abandonPendingSubmit();
      updateDetails((prev) => ({
        ...prev,
        data: { ...prev.data, rows: mergeReplacedUploadRows(prev, parsedRows, state.createdProjects) },
        validationErrors: [],
      }));
    },
    [abandonPendingSubmit, editDetails, isPartiallyCreated, state.createdProjects, updateDetails]
  );

  // A submit still running is about the file being replaced, so its answer is already stale.
  const handleUploadModalOpen = useCallback(() => {
    abandonPendingSubmit();
    clearErrors();
  }, [abandonPendingSubmit, clearErrors]);

  const uploadModal = useBulkUploadModal({ options, onParsed: handleParsedRows, onOpen: handleUploadModalOpen });

  const handleLandholdingChoiceChange = useCallback(
    (choice: boolean) => editDetails((prev) => ({ ...prev, isLandholding: choice })),
    [editDetails]
  );

  const handleLandholdingChange = useCallback(
    (value: LandholdingDetails) => editDetails((prev) => ({ ...prev, data: { ...prev.data, landholding: value } })),
    [editDetails]
  );

  const handleRowChange = useCallback(
    (row: ProjectRow) =>
      editDetails((prev) => ({
        ...prev,
        data: {
          ...prev.data,
          rows: prev.data.rows.map((existing) =>
            existing.key === row.key ? reconcileRowSelections(existing, row, options) : existing
          ),
        },
      })),
    [editDetails, options]
  );

  // The new row is left out of the refresh: it is untouched, so reporting it as missing required
  // fields the moment it appears would be reporting a mistake the user has not made yet.
  const handleAddProject = useCallback(
    () =>
      editDetails((prev) => ({ ...prev, data: { ...prev.data, rows: [...prev.data.rows, emptyManualProjectRow()] } })),
    [editDetails]
  );

  const handleRemoveProject = useCallback(
    (key: string) => {
      if (rows.length <= 1) return;

      editDetails((prev) => ({
        ...prev,
        data: { ...prev.data, rows: prev.data.rows.filter((row) => row.key !== key) },
      }));
    },
    [editDetails, rows.length]
  );

  /*
    The api has the last word on both entry methods, through the very call the access step will save
    with - validateOnly true here when that step follows, so it runs every rule the save will run
    (BR-02881) and writes nothing (BR-02891); false when this step is the whole journey, so the same
    call creates. Each row goes with the uuid this step minted, as the save will send it.

    It is the only check either method gets. Whether a name is long enough, a standard one this
    organisation can create under, or a type the standard carries, is the api's answer to give, and
    saying it twice only risks the two disagreeing.
  */
  const submitToApi = useCallback(async (): Promise<SubmitProjectDetailsResult | null> => {
    submitRequestIdRef.current += 1;
    const requestId = submitRequestIdRef.current;

    setIsSubmitting(true);

    try {
      // No developer: the api takes the creating organisation from the caller, and a developer is
      // only named where the access step's list hands the role to another organisation - which
      // this step has no access list to know about.
      const result = await callProjectDetails(details, () => null, !isLastStep, state.createdProjects);

      // A newer press already owns the step; this answer is about rows that may no longer be there.
      if (requestId !== submitRequestIdRef.current) return null;

      // A final-step save that only partly went through has written projects the next press must
      // not create again, so what it wrote is kept before the errors are shown (BR-02888).
      if (isLastStep && hasCreatedAnything(result.created)) setCreatedProjects(result.created);

      setValidationErrors(result.errors);
      return result.errors.length === 0 ? result : null;
    } finally {
      if (requestId === submitRequestIdRef.current) setIsSubmitting(false);
    }
  }, [details, isLastStep, setCreatedProjects, setValidationErrors, state.createdProjects]);

  const handleNext = useCallback(() => {
    // Nothing to check or create: the rows already exist. Back to where the invitations are sent.
    // (When this step is the whole journey there is nowhere to go back to, so a locked step falls
    // through to the save, which skips what already exists and retries the rest.)
    if (isLocked && !isLastStep) {
      completeStep(NewProjectJourneyStep.DETAILS);
      return;
    }

    clearErrors();

    // Only the standards are indispensable - a verifier or country outage leaves the step usable,
    // and its error box is already on screen. Without them there is nothing valid to enter, so stop
    // rather than report every row as missing required fields.
    if (options.hasLoadFailed && options.standardOptions.length === 0) return;

    if (!isManual && rows.length === 0) {
      setValidationErrors([UPLOAD_REQUIRED_ERROR]);
      return;
    }

    submitToApi()
      .then((result) => {
        if (!result) return;

        if (!isLastStep) {
          completeStep(NewProjectJourneyStep.DETAILS);
          return;
        }

        // Everything is written, so the journey must not carry its entries into the next visit.
        // The host decides what the user sees next.
        clearJourney();
        onComplete({
          ...result.created,
          projectCount: Object.keys(result.created.projectUuidByKey).length,
          invitationCount: 0,
          isLandholding,
          rows,
          landholding: isLandholding ? landholding : null,
        });
      })
      .catch(() => {
        setIsSubmitting(false);
        setValidationErrors([
          {
            code: "UNEXPECTED_ERROR",
            message: isLastStep
              ? "We couldn't save your projects just now. Please try again."
              : "We couldn't check your projects just now. Please try again.",
          },
        ]);
      });
  }, [
    clearErrors,
    clearJourney,
    completeStep,
    isLandholding,
    isLastStep,
    isLocked,
    isManual,
    landholding,
    onComplete,
    options.hasLoadFailed,
    options.standardOptions,
    rows,
    setValidationErrors,
    submitToApi,
  ]);

  const switchProjectCount = useMemo(
    () => (entryMethodSwitch.pendingMethod ? getEnteredRowCount(details, details.entryMethod) : 0),
    [details, entryMethodSwitch.pendingMethod]
  );

  return {
    variant,
    corners,
    entryMethodResetKey: entryMethodSwitch.toggleResetKey,
    entryMethod: details.entryMethod,
    isLandholding: details.isLandholding,
    rows,
    uploadRowErrors,
    landholding,
    formErrors,
    rowErrors,
    landholdingErrors,
    showSwitchModal: entryMethodSwitch.pendingMethod !== null,
    showUploadModal: uploadModal.showUploadModal,
    isParsing: uploadModal.isParsing,
    isSubmitting,
    isLocked,
    isPartiallyCreated,
    isReadOnly,
    nextLabel: isLastStep ? "Save" : "Next",
    submittingMessage: isLastStep
      ? "Creating your projects. This can take a moment for a large file."
      : "Checking your projects. This can take a moment for a large file.",
    parseError: uploadModal.parseError,
    showOutOfDateTemplateHint: uploadModal.showOutOfDateTemplateHint,
    switchProjectCount,
    optionsLoadFailed: options.hasLoadFailed,
    verifierOptions: options.verifierOptions,
    countryOptions: options.countryOptions,
    getStandardOptions: options.getStandardOptions,
    getProjectTypeOptions: options.getProjectTypeOptions,
    handleEntryMethodChange: entryMethodSwitch.requestSwitch,
    handleConfirmDiscard: entryMethodSwitch.confirmSwitch,
    handleKeepEntries: entryMethodSwitch.keepEntries,
    handleLandholdingChoiceChange,
    handleLandholdingChange,
    handleRowChange,
    handleAddProject,
    handleRemoveProject,
    handleDownloadTemplate: uploadModal.handleDownloadTemplate,
    handleFileSelected: uploadModal.handleFileSelected,
    handleUpload: uploadModal.handleUpload,
    handleOpenUploadModal: uploadModal.handleOpenUploadModal,
    handleCancelUpload: uploadModal.handleCancelUpload,
    handleRetryOptions: options.reload,
    handleNext,
  };
};
